import { CommonModule, DatePipe } from '@angular/common';
import { Component, OnInit, effect, inject, untracked } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { ToastService } from 'angular-toastify';
import { CookieService } from 'ngx-cookie-service';
import { PssLoadingComponent } from '../../../../tools/template/pss/pss-loading/pss-loading.component';
import { CityParkingCards } from '../../../../core/models/parking-card.model';
import {
  ParkingCardChartItem,
  ParkingCardDashboardStat,
  ParkingCardRecentActivityView,
} from '../../../../core/models/parking/parking-card-list.model';
import { MapperParkingCardListService } from '../../../../core/mappers/parking/mapper-parking-card-list.service';
import { ApiParkingCardListService } from '../../../../core/services/api/parking/api-parking-card-list.service';
import { ApiSystemSettingsService } from '../../../../core/services/api/system-settings/api-system-settings.service';
import { SiteStateService } from '../../../../core/services/site-state.service';
import { MenuPermissionService } from '../../../../core/services/menu-permission.service';
import { ApiService } from '../../../../core/services/api.service';
import { ClientIInfo } from '../../../../core/models/parking.model';
import { ParkingVehicleTypeResponse } from '../../../../core/models/vehicle.model';
import {
  AccessGroupScope,
  mapAccessGroupScope,
} from '../../../../core/models/system-settings/access-group.model';
import { ModalParkingCardDetailComponent } from '../../modals/parking/parking-card/modal-parking-card-detail/modal-parking-card-detail.component';
import { ModalParkingCardImportComponent } from '../../modals/parking/parking-card/modal-parking-card-import/modal-parking-card-import.component';
import { PssConfirmModalComponent } from '../../../../tools/template/pss/pss-confirm-modal/pss-confirm-modal.component';
import * as XLSX from 'xlsx';

@Component({
  selector: 'app-parking-cards',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ModalParkingCardDetailComponent,
    ModalParkingCardImportComponent,
    PssLoadingComponent,
    PssConfirmModalComponent,
  ],
  templateUrl: './parking-cards.component.html',
  styleUrl: './parking-cards.component.css',
  providers: [DatePipe],
})
export class ParkingCardsComponent implements OnInit {
  readonly siteState = inject(SiteStateService);
  private readonly apiService = inject(ApiParkingCardListService);
  private readonly apiLegacy = inject(ApiService);
  private readonly apiSettings = inject(ApiSystemSettingsService);
  private readonly mapper = inject(MapperParkingCardListService);
  private readonly toast = inject(ToastService);
  private readonly cookieService = inject(CookieService);
  private readonly datePipe = inject(DatePipe);
  private readonly router = inject(Router);
  private readonly permissionService = inject(MenuPermissionService);

  parkingCards: CityParkingCards[] = [];
  protected Math = Math;

  searchText = '';
  searchType: 'card_code' | 'card_name' | 'name' | 'uid' | 'license' = 'license';
  isSearchTypeDropdownOpen = false;
  filterStatus = 0;

  clients: ClientIInfo[] = [];
  selectedClientId = 0;
  clientSearchText = '';
  isClientDropdownOpen = false;

  vehicleTypes: ParkingVehicleTypeResponse[] = [];
  selectedVehicleTypeId = 0;
  vehicleTypeSearchText = '';
  isVehicleTypeDropdownOpen = false;

  isFormModalOpen = false;
  modalMode: 'create' | 'view' | 'edit' = 'create';
  selectedCardId: number | null = null;
  isImportModalOpen = false;

  isDeleteConfirmOpen = false;
  cardPendingDelete: CityParkingCards | null = null;
  deleteRemark = '';
  isDeleting = false;

  sortColumn = 'id';
  sortDirection: 'asc' | 'desc' = 'desc';

  currentPage = 1;
  pageSize = 20;
  pageSizeOptions: number[] = [20, 50, 100];
  totalItems = 0;
  totalPages = 1;

  isLoading = false;
  isDashboardLoading = false;
  showDashboard = true;
  showRatioChart = true;
  dashboardStats: ParkingCardDashboardStat[] = [];
  activePercent = 0;
  expiredPercent = 0;
  cancelledPercent = 0;
  recentActivity: ParkingCardRecentActivityView[] = [];
  dashboardData: ParkingCardChartItem[] = [];

  chartMode: 'daily' | 'monthly' = 'daily';
  dailySubMode: 'default' | 'custom' = 'default';
  chartType: 'line' | 'bar' = 'line';
  chartWidth = '100%';
  customStartDate = '';
  customEndDate = '';
  selectedMonthStart = new Date().toISOString().slice(0, 7);
  selectedMonthEnd = new Date().toISOString().slice(0, 7);
  yAxisLabels: number[] = [];
  chartTotalCount = 0;
  currentMonthPoints = '';
  currentMonthAreaPoints = '';
  currentMonthDots: Array<{ x: number; y: number; label: string; subLabel: string; value: number; color: string }> = [];

  userId = 0;
  accessScope: AccessGroupScope | null = null;

  toggleDashboard(): void {
    this.showDashboard = !this.showDashboard;
  }

  constructor() {
    const userIdLocal = localStorage.getItem('userId');
    this.userId = userIdLocal
      ? Number(userIdLocal)
      : Number(this.cookieService.get('user_id'));

    effect(() => {
      const site = this.siteState.selectedSite();
      if (!site?.id || !this.userId) return;

      untracked(() => {
        this.currentPage = 1;
        this.reloadForSite();
      });
    });
  }

  ngOnInit(): void {
    if (!this.userId) {
      const userIdLocal = localStorage.getItem('userId');
      this.userId = userIdLocal
        ? Number(userIdLocal)
        : Number(this.cookieService.get('user_id'));
    }
    if (this.selectedSiteId && this.userId) {
      this.reloadForSite();
    }
  }

  private reloadForSite(): void {
    this.loadAccessScope(() => {
      this.loadClients();
      this.loadVehicleTypes();
      this.loadTable();
      this.loadMarketingDashboard();
    });
  }

  private loadAccessScope(done?: () => void): void {
    const siteId = this.selectedSiteId;
    if (!siteId || !this.userId) {
      this.accessScope = null;
      done?.();
      return;
    }
    this.apiSettings.getMyAccessGroupScope(this.userId, siteId).subscribe({
      next: (res) => {
        this.accessScope = mapAccessGroupScope(res);
        done?.();
      },
      error: () => {
        this.accessScope = null;
        done?.();
      },
    });
  }

  get isAccessScoped(): boolean {
    return !!(this.accessScope?.groupId);
  }

  get accessScopeLabel(): string {
    if (!this.accessScope) return '';
    return this.accessScope.groupName || `กลุ่ม #${this.accessScope.groupId}`;
  }

  private static readonly CLIENT_OPTIONS_PREVIEW = 20;
  private static readonly VEHICLE_TYPE_OPTIONS_PREVIEW = 20;

  get clientFilterOptions(): ClientIInfo[] {
    const sorted = [...this.clients].sort((a, b) => a.name.localeCompare(b.name, 'th'));
    const q = this.clientSearchText.trim().toLowerCase();
    if (!q) return sorted.slice(0, ParkingCardsComponent.CLIENT_OPTIONS_PREVIEW);
    return sorted.filter((c) => c.name.toLowerCase().includes(q));
  }

  loadClients(): void {
    const siteId = this.selectedSiteId;
    if (!siteId) {
      this.clients = [];
      return;
    }
    this.apiLegacy.GetClientIInfo(siteId).subscribe({
      next: (res) => {
        let list = res ?? [];
        const allowed = this.accessScope?.clientIds;
        if (allowed != null) {
          const set = new Set(allowed);
          list = list.filter((c) => set.has(c.id));
        }
        this.clients = list;
        if (this.selectedClientId > 0) {
          const selected = this.clients.find((c) => c.id === this.selectedClientId);
          if (selected) {
            this.clientSearchText = selected.name;
          } else {
            this.selectedClientId = 0;
            this.clientSearchText = '';
          }
        }
      },
      error: () => (this.clients = []),
    });
  }

  openClientDropdown(): void {
    this.isClientDropdownOpen = true;
  }

  closeClientDropdown(): void {
    this.isClientDropdownOpen = false;
    if (this.selectedClientId > 0) {
      const selected = this.clients.find((c) => c.id === this.selectedClientId);
      if (selected) {
        this.clientSearchText = selected.name;
      }
    }
  }

  onClientSearchInput(): void {
    this.isClientDropdownOpen = true;
  }

  selectClient(client: ClientIInfo): void {
    this.selectedClientId = client.id;
    this.clientSearchText = client.name;
    this.isClientDropdownOpen = false;
    this.currentPage = 1;
    this.loadTable();
  }

  clearClientFilter(): void {
    this.selectedClientId = 0;
    this.clientSearchText = '';
    this.isClientDropdownOpen = false;
    this.currentPage = 1;
    this.loadTable();
  }

  get vehicleTypeFilterOptions(): ParkingVehicleTypeResponse[] {
    const sorted = [...this.vehicleTypes].sort((a, b) => a.typeName.localeCompare(b.typeName, 'th'));
    const q = this.vehicleTypeSearchText.trim().toLowerCase();
    if (!q) return sorted.slice(0, ParkingCardsComponent.VEHICLE_TYPE_OPTIONS_PREVIEW);
    return sorted.filter((v) => v.typeName.toLowerCase().includes(q));
  }

  loadVehicleTypes(): void {
    this.apiService.getParkingVehicleTypes().subscribe({
      next: (res) => {
        this.vehicleTypes = (res ?? []).map((row) => {
          const raw = row as ParkingVehicleTypeResponse & Record<string, unknown>;
          return {
            id: row.id ?? (raw['Id'] as number) ?? 0,
            typeName: row.typeName ?? (raw['TypeName'] as string) ?? '',
            icon: row.icon ?? (raw['Icon'] as string) ?? '',
          };
        });
        if (this.selectedVehicleTypeId > 0) {
          const selected = this.vehicleTypes.find((v) => v.id === this.selectedVehicleTypeId);
          if (selected) {
            this.vehicleTypeSearchText = selected.typeName;
          }
        }
      },
      error: () => (this.vehicleTypes = []),
    });
  }

  openVehicleTypeDropdown(): void {
    this.isVehicleTypeDropdownOpen = true;
  }

  closeVehicleTypeDropdown(): void {
    this.isVehicleTypeDropdownOpen = false;
    if (this.selectedVehicleTypeId > 0) {
      const selected = this.vehicleTypes.find((v) => v.id === this.selectedVehicleTypeId);
      if (selected) {
        this.vehicleTypeSearchText = selected.typeName;
      }
    }
  }

  onVehicleTypeSearchInput(): void {
    this.isVehicleTypeDropdownOpen = true;
  }

  selectVehicleType(vehicleType: ParkingVehicleTypeResponse): void {
    this.selectedVehicleTypeId = vehicleType.id;
    this.vehicleTypeSearchText = vehicleType.typeName;
    this.isVehicleTypeDropdownOpen = false;
    this.currentPage = 1;
    this.loadTable();
  }

  clearVehicleTypeFilter(): void {
    this.selectedVehicleTypeId = 0;
    this.vehicleTypeSearchText = '';
    this.isVehicleTypeDropdownOpen = false;
    this.currentPage = 1;
    this.loadTable();
  }

  get selectedSiteId(): number {
    return this.siteState.selectedSite()?.id ?? 0;
  }

  get canCreate(): boolean {
    if (this.isAccessScoped && !this.accessScope?.canManageFreeCards) return false;
    return this.permissionService.canCreate(this.router.url);
  }

  get canUpdate(): boolean {
    if (this.isAccessScoped && !this.accessScope?.canManageFreeCards) return false;
    return this.permissionService.canUpdate(this.router.url);
  }

  get canDelete(): boolean {
    if (this.isAccessScoped && !this.accessScope?.canDeleteCardReturnQuota) return false;
    return this.permissionService.canDelete(this.router.url);
  }

  navigateToCreate(): void {
    if (!this.canCreate) {
      this.toast.error('ไม่มีสิทธิ์สร้าง');
      return;
    }
    if (!this.selectedSiteId) {
      this.toast.error('กรุณาเลือก Site จาก Sidebar ก่อน');
      return;
    }
    this.modalMode = 'create';
    this.selectedCardId = null;
    this.isFormModalOpen = true;
  }

  openImportModal(): void {
    if (!this.canCreate) {
      this.toast.error('ไม่มีสิทธิ์สร้าง');
      return;
    }
    if (!this.selectedSiteId) {
      this.toast.error('กรุณาเลือก Site จาก Sidebar ก่อน');
      return;
    }
    this.isImportModalOpen = true;
  }

  closeImportModal(): void {
    this.isImportModalOpen = false;
  }

  onCardsImported(): void {
    this.loadTable();
    this.loadMarketingDashboard();
  }

  downloadSampleExcel(): void {
    const sampleData = [
      {
        licensePlate: 'กข1234',
        province: 'กรุงเทพมหานคร',
        name: 'สมชาย ใจดี',
        phone: '0812345678',
        address: '123 ถ.พระราม 9 กรุงเทพฯ',
      },
      {
        licensePlate: '1กก9999',
        province: 'นนทบุรี',
        name: 'สมหญิง รักดี',
        phone: '0898765432',
        address: '99 หมู่ 1 ต.บางกรวย',
      },
    ];
    const headers = ['licensePlate', 'province', 'name', 'phone', 'address'];
    const worksheet = XLSX.utils.json_to_sheet(sampleData, { header: headers });
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'ParkingCards');
    XLSX.writeFile(workbook, 'ParkingCards_Import_Template.xlsx');
  }

  openEditModal(card: CityParkingCards): void {
    if (!card?.id) return;
    if (!this.canUpdate) {
      this.toast.error('ไม่มีสิทธิ์แก้ไข');
      return;
    }
    this.modalMode = 'edit';
    this.selectedCardId = card.id;
    this.isFormModalOpen = true;
  }

  openViewModal(card: CityParkingCards): void {
    if (!card?.id) return;
    this.modalMode = 'view';
    this.selectedCardId = card.id;
    this.isFormModalOpen = true;
  }

  openCardModal(card: CityParkingCards): void {
    if (!card?.id) return;
    if (this.canUpdate) {
      this.openEditModal(card);
      return;
    }
    this.openViewModal(card);
  }

  closeFormModal(): void {
    this.isFormModalOpen = false;
    this.selectedCardId = null;
  }

  onCardSaved(): void {
    this.closeFormModal();
    this.loadTable();
    this.loadMarketingDashboard();
  }

  openDeleteConfirm(card: CityParkingCards): void {
    if (!card?.id) return;
    if (!this.canDelete) {
      this.toast.error('ไม่มีสิทธิ์ลบ');
      return;
    }
    if (!this.selectedSiteId) {
      this.toast.error('กรุณาเลือก Site จาก Sidebar ก่อน');
      return;
    }
    this.cardPendingDelete = card;
    this.deleteRemark = '';
    this.isDeleteConfirmOpen = true;
  }

  closeDeleteConfirm(): void {
    if (this.isDeleting) return;
    this.isDeleteConfirmOpen = false;
    this.cardPendingDelete = null;
    this.deleteRemark = '';
  }

  confirmDeleteCard(): void {
    const card = this.cardPendingDelete;
    if (!card?.id || this.isDeleting) return;

    const remark = this.deleteRemark?.trim() ?? '';
    if (!remark) {
      this.toast.error('กรุณาระบุหมายเหตุ (Remark) ก่อนลบบัตร');
      return;
    }

    const menuId = this.permissionService.getMenuId(this.router.url);
    if (!this.userId || !menuId) {
      this.toast.error('ไม่พบสิทธิ์เมนู');
      return;
    }

    this.isDeleting = true;
    this.apiService.deleteParkingCard({
      cardId: card.id,
      siteId: this.selectedSiteId,
      userId: this.userId,
      menuId,
      remark,
    }).subscribe({
      next: (res) => {
        this.isDeleting = false;
        if (res?.isSuccess) {
          this.toast.success(res.message || 'ลบบัตรสำเร็จ');
          this.closeDeleteConfirm();
          this.loadTable();
          this.loadMarketingDashboard();
          return;
        }
        this.toast.error(res?.message || 'ลบบัตรไม่สำเร็จ');
      },
      error: (err) => {
        this.isDeleting = false;
        this.toast.error(err?.error?.message || err?.error?.Message || 'ลบบัตรไม่สำเร็จ');
      },
    });
  }

  get deleteConfirmHighlight(): string {
    const c = this.cardPendingDelete;
    if (!c) return '';
    const parts = [c.cardName, c.cardCode].filter(Boolean);
    return parts.join(' · ') || `ID ${c.id}`;
  }

  sortData(column: string): void {
    if (this.sortColumn === column) {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortColumn = column;
      this.sortDirection = 'asc';
    }
    this.currentPage = 1;
    this.loadTable();
  }

  trackByCardId(index: number, item: CityParkingCards): number | string {
    const vehicleId = item.licenseList?.[0]?.vehicleId ?? 0;
    return item.id ? `${item.id}-${vehicleId}` : item.cardCode || index;
  }

  get filteredAndPaginatedData(): CityParkingCards[] {
    return this.parkingCards;
  }

  get startItemIndex(): number {
    if (this.totalItems === 0) return 0;
    return (this.currentPage - 1) * this.pageSize + 1;
  }

  get endItemIndex(): number {
    return Math.min(this.currentPage * this.pageSize, this.totalItems);
  }

  get pagesArray(): number[] {
    const maxVisibleButtons = 5;
    let startPage = Math.max(1, this.currentPage - 2);
    let endPage = Math.min(this.totalPages, startPage + maxVisibleButtons - 1);

    if (endPage - startPage < maxVisibleButtons - 1) {
      startPage = Math.max(1, endPage - maxVisibleButtons + 1);
    }

    return Array.from({ length: endPage - startPage + 1 }, (_, i) => startPage + i);
  }

  setPage(page: number): void {
    if (page >= 1 && page <= this.totalPages) {
      this.currentPage = page;
      this.loadTable();
    }
  }

  onPageSizeChange(event: Event): void {
    const element = event.target as HTMLSelectElement;
    this.pageSize = Number(element.value);
    this.currentPage = 1;
    this.loadTable();
  }

  onSearch(): void {
    this.currentPage = 1;
    this.loadTable();
  }

  onRefresh(): void {
    this.searchText = '';
    this.searchType = 'license';
    this.filterStatus = 0;
    this.selectedClientId = 0;
    this.clientSearchText = '';
    this.isClientDropdownOpen = false;
    this.selectedVehicleTypeId = 0;
    this.vehicleTypeSearchText = '';
    this.isVehicleTypeDropdownOpen = false;
    this.currentPage = 1;
    this.reloadForSite();
  }

  selectStatus(status: number): void {
    this.filterStatus = this.filterStatus === status ? 0 : status;
    this.currentPage = 1;
    this.loadTable();
  }

  toggleSearchTypeDropdown(): void {
    this.isSearchTypeDropdownOpen = !this.isSearchTypeDropdownOpen;
  }

  selectSearchType(type: 'card_name' | 'card_code' | 'name' | 'uid' | 'license'): void {
    this.searchType = type;
    this.isSearchTypeDropdownOpen = false;
  }

  get currentSearchTypeLabel(): string {
    switch (this.searchType) {
      case 'card_name': return 'หมายเลขบัตร';
      case 'card_code': return 'รหัสระบบ';
      case 'name': return 'ชื่อรายการ';
      case 'uid': return 'UID';
      case 'license': return 'ทะเบียนรถ';
      default: return 'ค้นหา';
    }
  }

  getPlateDisplay(card: CityParkingCards): string {
    const plate = card.licenseList?.[0]?.plateNumber?.trim();
    if (!plate) return '-';
    const province = card.licenseList?.[0]?.province?.trim();
    return province ? `${plate} ${province}` : plate;
  }

  hasPlate(card: CityParkingCards): boolean {
    return Boolean(card.licenseList?.[0]?.plateNumber?.trim());
  }

  /** Icon ข้างทะเบียน — แยก car / motorcycle จาก vehicleTypeName */
  getVehicleIcon(vehicleType: string | null | undefined): string {
    if (!vehicleType) return 'directions_car';
    const type = vehicleType.toLowerCase();
    if (
      type.includes('motor') ||
      type.includes('bike') ||
      type.includes('จักรยาน') ||
      type.includes('มอเตอร์')
    ) {
      return 'two_wheeler';
    }
    return 'directions_car';
  }

  getStatusText(status: number): string {
    switch (status) {
      case 0: return 'หมดอายุ';
      case 1: return 'ปกติ';
      case 2: return 'ยกเลิก';
      case 3: return 'ระงับใช้งาน';
      default: return 'หมดอายุ';
    }
  }

  getRowNumber(index: number): number {
    return (this.currentPage - 1) * this.pageSize + index + 1;
  }

  formatDate(date: Date): string {
    return this.datePipe.transform(date, 'yyyy-MM-dd') || '';
  }

  setChartMode(mode: 'daily' | 'monthly'): void {
    this.chartMode = mode;
    if (mode === 'monthly') {
      const today = new Date();
      const prevMonthDate = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      this.selectedMonthStart = prevMonthDate.toISOString().slice(0, 7);
      this.selectedMonthEnd = today.toISOString().slice(0, 7);
      this.onChartMonthChange();
      return;
    }
    this.processDashboardData();
  }

  setDailySubMode(mode: 'default' | 'custom'): void {
    this.dailySubMode = mode;
    if (mode === 'custom') {
      if (!this.customStartDate) {
        const s = new Date();
        s.setDate(s.getDate() - 6);
        this.customStartDate = this.formatDate(s);
      }
      if (!this.customEndDate) {
        const s = new Date(this.customStartDate);
        const e = new Date(this.customStartDate);
        e.setDate(s.getDate() + 6);
        this.customEndDate = this.formatDate(e);
      }
      this.onCustomDateChange();
    } else {
      this.processDashboardData();
    }
  }

  setChartType(type: 'line' | 'bar'): void {
    this.chartType = type;
  }

  get maxMonthEnd(): string {
    if (!this.selectedMonthStart) return '';
    const start = new Date(this.selectedMonthStart + '-01');
    const maxDate = new Date(start);
    maxDate.setMonth(maxDate.getMonth() + 11);
    return maxDate.toISOString().slice(0, 7);
  }

  onChartMonthChange(): void {
    if (this.chartMode !== 'monthly') return;

    if (this.selectedMonthStart && this.selectedMonthEnd) {
      const start = new Date(this.selectedMonthStart + '-01');
      const end = new Date(this.selectedMonthEnd + '-01');
      const diffMonths = (end.getFullYear() - start.getFullYear()) * 12 + (end.getMonth() - start.getMonth());

      if (diffMonths > 11) {
        const newEnd = new Date(start);
        newEnd.setMonth(start.getMonth() + 11);
        this.selectedMonthEnd = newEnd.toISOString().slice(0, 7);
      } else if (end < start) {
        this.selectedMonthEnd = this.selectedMonthStart;
      }
    }

    this.processDashboardData();
  }

  onCustomDateChange(): void {
    if (!this.customStartDate || !this.customEndDate) return;
    const s = new Date(this.customStartDate);
    const e = new Date(this.customEndDate);
    if (e < s) this.customEndDate = this.customStartDate;

    const diffDays = Math.ceil((e.getTime() - s.getTime()) / (1000 * 60 * 60 * 24));
    if (diffDays > 10) {
      const newEnd = new Date(s);
      newEnd.setDate(s.getDate() + 9);
      this.customEndDate = this.formatDate(newEnd);
    }
    this.processDashboardData();
  }

  get maxCustomDate(): string {
    if (!this.customStartDate) return '';
    const d = new Date(this.customStartDate);
    d.setDate(d.getDate() + 10);
    return this.formatDate(d);
  }

  resetCustomDate(): void {
    const e = new Date();
    const s = new Date();
    s.setDate(e.getDate() - 9);
    this.customEndDate = this.formatDate(e);
    this.customStartDate = this.formatDate(s);
    this.processDashboardData();
  }

  loadTable(): void {
    const siteId = this.selectedSiteId;
    if (!siteId || !this.userId) {
      this.parkingCards = [];
      this.totalItems = 0;
      this.totalPages = 1;
      return;
    }

    this.isLoading = true;
    const status = this.mapper.mapStatusFilter(this.filterStatus);

    this.apiService.getParkingCardForTable({
      siteId,
      userId: this.userId,
      clientId: this.selectedClientId > 0 ? this.selectedClientId : undefined,
      vehicleTypeId: this.selectedVehicleTypeId > 0 ? this.selectedVehicleTypeId : undefined,
      page: this.currentPage,
      pageSize: this.pageSize,
      searchField: this.searchText.trim() ? this.mapper.mapSearchField(this.searchType) : undefined,
      searchText: this.searchText.trim() || undefined,
      sortColumn: this.sortColumn,
      sortDirection: this.sortDirection,
      status: status ?? 'all',
    }).subscribe({
      next: (res) => {
        const mapped = this.mapper.mapTableFromDto(res);
        this.parkingCards = mapped.items;
        this.totalItems = mapped.total;
        this.totalPages = mapped.totalPages || Math.ceil(mapped.total / (this.pageSize || 1));
        this.isLoading = false;
      },
      error: (err) => {
        console.error('[API Error] getParkingCardForTable:', err);
        this.parkingCards = [];
        this.totalItems = 0;
        this.totalPages = 1;
        this.isLoading = false;
        this.toast.error('การดึงข้อมูลบัตรจอดรถล้มเหลว');
      }
    });
  }

  loadMarketingDashboard(): void {
    const siteId = this.selectedSiteId;
    if (!siteId || !this.userId) {
      this.resetDashboardView();
      return;
    }

    this.isDashboardLoading = true;
    this.apiService.getParkingCardMarketingDashboard(siteId, this.userId).subscribe({
      next: (res) => {
        this.applyMarketingDashboard(this.mapper.mapMarketingDashboard(res));
        this.isDashboardLoading = false;
      },
      error: (err) => {
        console.error('[API Error] getParkingCardMarketingDashboard:', err);
        this.resetDashboardView();
        this.isDashboardLoading = false;
      }
    });
  }

  private applyMarketingDashboard(view: ReturnType<MapperParkingCardListService['mapMarketingDashboard']>): void {
    this.dashboardStats = view.stats;
    this.activePercent = view.ratios.activePercent;
    this.expiredPercent = view.ratios.expiredPercent;
    this.cancelledPercent = view.ratios.cancelledPercent;
    this.dashboardData = view.chartItems;
    this.recentActivity = view.recentActivity.map(item => ({
      ...item,
      timeAgo: this.getTimeAgo(item.createDate ?? null),
      color: this.getActivityColor(item.id),
    }));
    this.processDashboardData();
  }

  private resetDashboardView(): void {
    this.dashboardStats = [];
    this.activePercent = 0;
    this.expiredPercent = 0;
    this.cancelledPercent = 0;
    this.recentActivity = [];
    this.dashboardData = [];
    this.chartTotalCount = 0;
    this.currentMonthPoints = '';
    this.currentMonthAreaPoints = '';
    this.currentMonthDots = [];
  }

  processDashboardData(): void {
    let xLabels: Date[] = [];
    let dataPoints: number[] = [];
    let diffDays = 0;

    if (this.chartMode === 'daily') {
      let start: Date;
      let end: Date;
      if (this.dailySubMode === 'custom' && this.customStartDate && this.customEndDate) {
        start = new Date(this.customStartDate);
        end = new Date(this.customEndDate);
      } else {
        start = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);
        end = new Date();
      }

      for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
        xLabels.push(new Date(d));
        dataPoints.push(0);
      }

      this.dashboardData.forEach(item => {
        const dateRaw = item.createDate || item.issueDate;
        if (!dateRaw) return;
        const d = new Date(dateRaw);
        const dDateOnly = new Date(d.getFullYear(), d.getMonth(), d.getDate());
        const startDateOnly = new Date(start.getFullYear(), start.getMonth(), start.getDate());
        const endDateOnly = new Date(end.getFullYear(), end.getMonth(), end.getDate());

        if (dDateOnly >= startDateOnly && dDateOnly <= endDateOnly) {
          const idx = xLabels.findIndex(x => x.toDateString() === dDateOnly.toDateString());
          if (idx !== -1) dataPoints[idx]++;
        }
      });
    } else {
      const [startYear, startMonth] = this.selectedMonthStart.split('-').map(Number);
      const [endYear, endMonth] = this.selectedMonthEnd.split('-').map(Number);
      const start = new Date(startYear, startMonth - 1, 1);
      const end = new Date(endYear, endMonth, 0);

      const diffTime = Math.abs(end.getTime() - start.getTime());
      diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

      if (diffDays <= 60) {
        for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
          xLabels.push(new Date(d));
          dataPoints.push(0);
        }
        this.dashboardData.forEach(item => {
          const dateRaw = item.createDate || item.issueDate;
          if (!dateRaw) return;
          const d = new Date(dateRaw);
          if (d >= start && d <= end) {
            const idx = xLabels.findIndex(x => x.toDateString() === d.toDateString());
            if (idx !== -1) dataPoints[idx]++;
          }
        });
      } else {
        let temp = new Date(startYear, startMonth - 1, 1);
        while (temp <= end) {
          xLabels.push(new Date(temp));
          dataPoints.push(0);
          temp.setMonth(temp.getMonth() + 1);
        }
        this.dashboardData.forEach(item => {
          const dateRaw = item.createDate || item.issueDate;
          if (!dateRaw) return;
          const d = new Date(dateRaw);
          if (d < start || d > end) return;
          const idx = xLabels.findIndex(x => x.getFullYear() === d.getFullYear() && x.getMonth() === d.getMonth());
          if (idx !== -1) dataPoints[idx]++;
        });
      }
    }

    this.chartTotalCount = dataPoints.reduce((a, b) => a + b, 0);

    const rawMax = Math.max(...dataPoints, 5);
    const maxVal = Math.ceil(rawMax / 4) * 4;
    this.yAxisLabels = [0, maxVal * 0.25, maxVal * 0.5, maxVal * 0.75, maxVal];

    const getCoordinate = (val: number, index: number, total: number) => {
      if (total <= 1) return { x: 50, y: 100 - ((val / maxVal) * 100) };
      const x = 2 + (index / (total - 1)) * 96;
      const y = 100 - ((val / maxVal) * 100);
      return { x, y };
    };

    const pointsObj = dataPoints.map((val, i) => getCoordinate(val, i, dataPoints.length));
    this.currentMonthPoints = pointsObj.map(p => `${p.x},${p.y}`).join(' ');

    const getThaiMonth = (monthIndex: number) => {
      const months = ['ม.ค.', 'ก.พ.', 'มี.ค.', 'เม.ย.', 'พ.ค.', 'มิ.ย.', 'ก.ค.', 'ส.ค.', 'ก.ย.', 'ต.ค.', 'พ.ย.', 'ธ.ค.'];
      return months[monthIndex];
    };

    this.currentMonthDots = pointsObj.map((p, i) => {
      const date = xLabels[i];
      let label = `${date.getDate()}`;
      let subLabel = '';

      if (date.getDate() === 1 || i === 0) {
        subLabel = getThaiMonth(date.getMonth());
      }

      if (this.chartMode === 'monthly' && dataPoints.length <= 12 && diffDays > 60) {
        label = getThaiMonth(date.getMonth());
        subLabel = `${date.getFullYear() + 543}`.slice(2);
      }

      return {
        x: p.x,
        y: p.y,
        label,
        subLabel,
        value: dataPoints[i],
        color: this.getChartColor(i),
      };
    });

    if (pointsObj.length > 0) {
      this.currentMonthAreaPoints = `
          ${pointsObj[0].x},100
          ${this.currentMonthPoints}
          ${pointsObj[pointsObj.length - 1].x},100
       `;
    } else {
      this.currentMonthAreaPoints = '';
    }

    this.chartWidth = dataPoints.length > 30 ? `${dataPoints.length * 40}px` : '100%';
  }

  private getActivityColor(id: number): string {
    const colors = ['bg-blue-500', 'bg-green-500', 'bg-yellow-500', 'bg-purple-500', 'bg-pink-500', 'bg-indigo-500'];
    return colors[id % colors.length];
  }

  private getChartColor(index: number): string {
    const colors = ['#fbbf24', '#f59e0b', '#34d399', '#60a5fa', '#f87171', '#818cf8', '#a78bfa', '#f472b6'];
    return colors[index % colors.length];
  }

  private getTimeAgo(dateInput: string | Date | null): string {
    if (!dateInput) return '-';
    const date = new Date(dateInput);
    if (isNaN(date.getTime())) return '-';
    const now = new Date();
    const seconds = Math.floor((now.getTime() - date.getTime()) / 1000);

    let interval = seconds / 31536000;
    if (interval > 1) return Math.floor(interval) + ' ปีที่แล้ว';
    interval = seconds / 2592000;
    if (interval > 1) return Math.floor(interval) + ' เดือนที่แล้ว';
    interval = seconds / 86400;
    if (interval > 1) return Math.floor(interval) + ' วันที่แล้ว';
    interval = seconds / 3600;
    if (interval > 1) return Math.floor(interval) + ' ชั่วโมงที่แล้ว';
    interval = seconds / 60;
    if (interval > 1) return Math.floor(interval) + ' นาทีที่แล้ว';
    return 'เพิ่งสร้าง';
  }
}
